/**
 * For the host of a paid event: "3 paid · 2 unpaid" at a glance, and (when
 * `remindUrl` is given) a button that notifies every unpaid player at once.
 * Unpaid players are never removed automatically: the host decides.
 */

import React, { useState } from 'react';
import { apiClient } from '../../services/api/apiService';

const countBy = (statuses = []) => {
  const counts = { paid: 0, unpaid: 0, refunded: 0 };
  for (const s of statuses) {
    if (s === 'paid') counts.paid += 1;
    else if (s === 'refunded') counts.refunded += 1;
    else counts.unpaid += 1;
  }
  return counts;
};

export function paidCounts(statuses) {
  return countBy(statuses);
}

export default function PaidSummary({ statuses, remindUrl, note }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const { paid, unpaid, refunded } = countBy(statuses);
  if (paid + unpaid + refunded === 0) return null;

  const remind = async () => {
    setSending(true);
    try {
      const r = await apiClient.post(remindUrl);
      setResult({ ok: true, text: r.data?.message || r.data?.data?.message || 'Reminder sent' });
    } catch (err) {
      setResult({ ok: false, text: err.response?.data?.message || 'Could not send the reminder' });
    }
    setSending(false);
  };

  return (
    <div data-testid="paid-summary" style={{ margin: '0.4rem 0 0.6rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.88rem' }}>
        <span style={{ color: '#7dd3fc' }}>✅ {paid} paid</span>
        <span style={{ color: unpaid ? '#fcd34d' : '#94a3b8' }}>⏳ {unpaid} unpaid</span>
        {refunded > 0 && <span style={{ color: '#94a3b8' }}>↩️ {refunded} refunded</span>}
        {remindUrl && unpaid > 0 && (
          <button
            type="button"
            onClick={remind}
            disabled={sending}
            data-testid="remind-unpaid"
            style={{
              padding: '0.25rem 0.7rem', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.8rem',
              background: 'rgba(252,211,77,0.15)', border: '1px solid #fcd34d', color: '#fcd34d',
            }}
          >
            {sending ? 'Sending…' : '🔔 Remind unpaid'}
          </button>
        )}
      </div>
      {note && unpaid > 0 && <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '0.3rem 0 0' }}>{note}</p>}
      {result && (
        <p role={result.ok ? 'status' : 'alert'} style={{ fontSize: '0.8rem', margin: '0.3rem 0 0', color: result.ok ? '#86efac' : '#fca5a5' }}>
          {result.text}
        </p>
      )}
    </div>
  );
}
